import { useEffect, useState } from 'react'
import BotonSesion from './BotonSesion'
import Enlace from './Enlace'
import { navegacion } from '../data/contenido'

function Encabezado() {
  const [abierto, setAbierto] = useState(false)
  const [desplazado, setDesplazado] = useState(false)

  useEffect(() => {
    const alDesplazar = () => setDesplazado(window.scrollY > 24)
    alDesplazar()
    window.addEventListener('scroll', alDesplazar, { passive: true })
    return () => window.removeEventListener('scroll', alDesplazar)
  }, [])

  // Al pasar a escritorio el menú móvil se oculta por CSS, pero el estado
  // seguía en `abierto`: al rotar de vuelta a vertical reaparecía solo.
  // Se cierra en cuanto se cruza el breakpoint lg (mismo punto en el que el
  // header pasa de menú hamburguesa a nav horizontal — ver comentario junto
  // a `hidden lg:block` más abajo).
  useEffect(() => {
    const consulta = window.matchMedia('(min-width: 64rem)')
    const alCambiar = (evento) => {
      if (evento.matches) setAbierto(false)
    }
    consulta.addEventListener('change', alCambiar)
    return () => consulta.removeEventListener('change', alCambiar)
  }, [])

  // Con el menú abierto, el fondo ya no debe desplazarse, y Escape lo cierra.
  useEffect(() => {
    if (!abierto) return

    const alPulsar = (evento) => {
      if (evento.key === 'Escape') setAbierto(false)
    }
    const overflowPrevio = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', alPulsar)

    return () => {
      document.body.style.overflow = overflowPrevio
      document.removeEventListener('keydown', alPulsar)
    }
  }, [abierto])

  return (
    <header
      className={[
        'fixed inset-x-0 top-0 z-40 transition-colors duration-300',
        // El menú móvil desplegado necesita fondo sólido aunque no se haya
        // hecho scroll todavía; si no, el contenido se lee por detrás.
        desplazado || abierto
          ? 'border-b border-oro-profundo/30 bg-noche/90 backdrop-blur-md'
          : 'border-b border-transparent',
      ].join(' ')}
    >
      <div className="contenedor flex h-(--altura-encabezado) items-center justify-between gap-4">
        {/*
          shrink-0 + whitespace-nowrap: con 8 enlaces en el nav más el botón
          de sesión, el logo llegó a quedarse sin espacio y el texto
          "Bengalí Leopard Cats" partía en dos líneas — se ve roto en un
          header de una sola fila. El logo nunca debe ceder espacio ni
          envolver: si algo cede, es el nav (ver `hidden lg:block`).
        */}
        <Enlace
          href="/"
          className="flex shrink-0 items-center gap-2.5 font-display text-base tracking-wide text-marfil transition-colors hover:text-oro-claro sm:text-lg"
        >
          <img
            src="/logo-icono.webp"
            alt=""
            className="size-[3.0418rem] shrink-0 rounded-full object-contain sm:size-[3.4219rem]"
          />
          <span className="whitespace-nowrap">
            Bengalí <span className="text-oro">Leopard Cats</span>
          </span>
        </Enlace>

        {/*
          8 enlaces + logo + botón de sesión no caben en una sola fila antes
          de ~1024px sin apretarse (los de más de una palabra —"El
          criadero", "Quiénes somos"— partían en dos líneas). Antes el nav
          aparecía desde md (768px); se sube a lg para que solo se muestre
          cuando de verdad hay aire, y hasta ahí el menú hamburguesa (ya
          probado y funcional) hace el trabajo. gap-5 en vez de gap-6 justo
          en lg le da un poco más de margen al primer tramo donde aparece.
        */}
        <nav aria-label="Principal" className="hidden lg:block">
          <ul className="flex items-center gap-5 xl:gap-8">
            {navegacion.map((item) => (
              <li key={item.href}>
                <Enlace
                  href={item.href}
                  className="whitespace-nowrap text-sm text-marfil-suave transition-colors hover:text-oro-claro"
                >
                  {item.etiqueta}
                </Enlace>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex shrink-0 items-center gap-3 sm:gap-4">
          <BotonSesion />

          {/* -m-2 p-2 amplía el área táctil a 40px sin mover el icono ni
              desalinear la fila. */}
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            aria-expanded={abierto}
            aria-controls="menu-movil"
            aria-label={abierto ? 'Cerrar menú' : 'Abrir menú'}
            className="area-tactil -m-2 p-2 text-marfil-suave transition-colors hover:text-oro-claro lg:hidden"
          >
            <svg
              className="size-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              {abierto ? (
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {abierto && (
        <nav
          id="menu-movil"
          aria-label="Principal móvil"
          // max-h + scroll: en horizontal sobre un teléfono la pantalla mide
          // ~380px de alto y los ocho enlaces no caben.
          className="max-h-[calc(100svh-var(--altura-encabezado))] overflow-y-auto overscroll-contain border-t border-oro-profundo/30 bg-noche/95 backdrop-blur-md lg:hidden"
        >
          <ul className="contenedor py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
            {navegacion.map((item) => (
              <li key={item.href}>
                <Enlace
                  href={item.href}
                  onClick={() => setAbierto(false)}
                  className="flex min-h-11 items-center text-base text-marfil-suave transition-colors hover:text-oro-claro"
                >
                  {item.etiqueta}
                </Enlace>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  )
}

export default Encabezado
